import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getDeviceSync,
  getGetDeviceSyncQueryKey,
  type CustomFetchOptions,
  type DeviceSync,
} from "@workspace/api-client-react";
import { isOfflineCapablePlayer } from "@/lib/platform";
import { syncHorizonMinutes } from "@workspace/player-shell";
import {
  enforceCacheCap,
  loadLastSync,
  loadSyncVersion,
  offlineReadiness,
  saveLastSync,
  saveSyncVersion,
  prefetchMedia,
  pruneMedia,
} from "@/player/lib/offline-store";
import {
  msUntilEarliestUrlExpiry,
  syncNeedsUrlRefresh,
} from "@/player/lib/presigned-url-refresh";

export const SCHEDULE_UPDATED_EVENT = "omnimedia:schedule-updated";
export const DEVICE_UPDATED_EVENT = "omnimedia:device-updated";
/** Dispatched by the realtime hook on every reconnect (not the first connect). */
export const REALTIME_CONNECTED_EVENT = "omnimedia:realtime-connected";
/** Dispatched by the playback engine when it needs fresh presigned URLs now. */
export const FORCE_RESYNC_EVENT = "omnimedia:force-resync";
/**
 * Dispatched after a full prefetch/prune/cap cycle with the resulting offline
 * readiness. The player page listens and forwards it over the realtime socket.
 * Only fires on an offline-capable shell, so a browser tab stays silent and its
 * server-side offline_cache column stays NULL.
 */
export const CACHE_REPORT_EVENT = "omnimedia:cache-report";

/**
 * Pull the device's resolved schedule + referenced playlists (with playable,
 * pre-signed media URLs). Polls periodically so schedule changes propagate
 * without a reload.
 *
 * Offline support (T006): the last successful payload is mirrored to IndexedDB
 * and its media is pre-cached. When the network is down the hook hydrates from
 * that cached payload so the engine keeps a valid program to run.
 */
export function useDeviceSync(enabled: boolean): {
  sync: DeviceSync | null;
  isLoading: boolean;
  error: Error | null;
  fromCache: boolean;
  refetch: () => void;
} {
  const offlineCapable = isOfflineCapablePlayer();

  const { data, isLoading, error, refetch } = useQuery({
    enabled,
    queryKey: getGetDeviceSyncQueryKey(),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<DeviceSync | null> => {
      const headers: HeadersInit = {};
      let params: { horizonMinutes?: number } | undefined;
      if (offlineCapable) {
        const cachedValidator = await loadSyncVersion();
        if (cachedValidator) {
          headers["If-None-Match"] = cachedValidator;
        }
        // Ask for the device's whole offline window (server clamps to the
        // device's own offlineCacheHours). Settings come from the mirrored
        // last sync; before the first sync we request the default window.
        const last = await loadLastSync();
        params = { horizonMinutes: syncHorizonMinutes(last?.device?.settings) };
      }
      // The conditional validator is the server's ETag, not the body
      // syncVersion: the ETag folds in a time bucket so a conditional poll
      // can never be frozen out of newly-in-horizon timed fires while the
      // spec-keyed syncVersion stays stable.
      let etag: string | null = null;
      const requestOptions: CustomFetchOptions = {
        headers,
        onResponse: (response) => {
          etag = response.headers.get("etag");
        },
      };
      const result = await getDeviceSync(params, requestOptions);
      if (result === null) {
        if (!offlineCapable) return null;
        return (await loadLastSync()) ?? null;
      }
      if (offlineCapable && etag) {
        await saveSyncVersion(etag);
      }
      return result;
    },
  });

  const [cached, setCached] = useState<DeviceSync | null>(null);

  const forceSync = useCallback(() => {
    void refetch();
  }, [refetch]);

  // Realtime pushes → immediate refresh (5-min poll is fallback). Reconnects
  // ride the same channel: a schedule edit made while the socket was down
  // would otherwise wait for the next poll. So do playback-engine requests
  // for fresh presigned URLs after media failures.
  useEffect(() => {
    if (!enabled) return;
    const onRefreshNeeded = () => forceSync();
    const events = [
      SCHEDULE_UPDATED_EVENT,
      DEVICE_UPDATED_EVENT,
      REALTIME_CONNECTED_EVENT,
      FORCE_RESYNC_EVENT,
    ];
    for (const event of events) {
      window.addEventListener(event, onRefreshNeeded);
    }
    return () => {
      for (const event of events) {
        window.removeEventListener(event, onRefreshNeeded);
      }
    };
  }, [enabled, forceSync]);

  // Hydrate the last persisted payload once so the engine has something to run
  // before (or instead of) a successful network fetch.
  useEffect(() => {
    if (!enabled || !offlineCapable) return;
    let active = true;
    void loadLastSync().then((s) => {
      if (active && s) setCached(s);
    });
    return () => {
      active = false;
    };
  }, [enabled, offlineCapable]);

  // Persist + pre-cache every fresh payload so the next outage is survivable.
  // Prefetch must complete before pruning: media URLs (R2 pre-signed) rotate
  // per sync, so pruning first could evict still-needed entries and leave a gap
  // if a network cut lands between the two steps. The offlineCacheMaxMb cap
  // runs last, after the cache holds everything the window needs.
  useEffect(() => {
    if (!data || !offlineCapable) return;
    void saveLastSync(data);
    void (async () => {
      await prefetchMedia(data);
      await pruneMedia(data);
      await enforceCacheCap(data);
      // Computed after the cap runs, so the report describes the cache that
      // actually survived rather than a mid-cycle state.
      const report = await offlineReadiness(data);
      if (report) {
        window.dispatchEvent(
          new CustomEvent(CACHE_REPORT_EVENT, { detail: report }),
        );
      }
    })();
  }, [data, offlineCapable]);

  const sync = data ?? cached;

  // Refresh presigned media URLs before they expire during long sessions.
  useEffect(() => {
    if (!enabled || !sync) return;
    if (syncNeedsUrlRefresh(sync)) {
      void refetch();
      return;
    }
    const ms = msUntilEarliestUrlExpiry(sync);
    if (ms === null) return;
    const timer = window.setTimeout(() => {
      void refetch();
    }, Math.max(0, ms));
    return () => window.clearTimeout(timer);
  }, [enabled, sync, refetch]);

  return {
    sync: sync ?? null,
    isLoading: enabled && isLoading && !cached,
    error: (error as Error) ?? null,
    fromCache: !data && Boolean(cached),
    refetch: forceSync,
  };
}
